/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React from 'react';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Rating, Skeleton } from '@material-ui/lab';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Grid,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { useStyles } from './styles';
import {
  API_TYPE_GRAPHQL,
  API_TYPE_WS,
  MAX_DESCRIPTION_LENGTH,
  PATH_SEPARATOR,
  RESOURCE_API,
  RESOURCE_API_DOCUMENTS,
  RESOURCE_API_TRYOUT,
  RESOURCE_OVERVIEW,
} from '../../../data/constants';
import { UserReducer } from '../../../data/interfaces';
import {
  trackApiSelectFromAPIList, trackDocumentationClickFromAPICard,
  trackTryOutClickFromAPICard
} from "../../../util/app-insights";
import Settings from '../../../conf/settings';

interface ApiCardProps {
  apiId: string;
  apiVersion: string;
  apiTitle: string;
  apiRating: number;
  apiSummary: string;
  apiImageUrl: string;
  organizationHandle: string;
  userReducer: UserReducer;
  lifeCycleStatus?: string;
  testId?: string;
  isInternalAPI?: boolean;
  apiType?: string;
}

function ApiCard(props: ApiCardProps) {
  const classes = useStyles();
  const history = useHistory();
  const {
    apiId,
    apiVersion,
    apiTitle,
    apiRating,
    apiSummary,
    apiImageUrl,
    organizationHandle,
    userReducer,
    lifeCycleStatus,
    testId,
    isInternalAPI,
    apiType,
  } = props;
  const isApimTokenObtained = userReducer.apimTokenObtained;
  const truncatedApiSummary =
    apiSummary?.length > MAX_DESCRIPTION_LENGTH
      ? apiSummary.substring(0, MAX_DESCRIPTION_LENGTH) + '...'
      : apiSummary;

  const intl = useIntl();

  const navigateToApiOverview = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (apiTitle) {
      trackApiSelectFromAPIList(`${apiId}`);
      history.push(
        PATH_SEPARATOR +
          organizationHandle +
          RESOURCE_API +
          PATH_SEPARATOR +
          apiId +
          RESOURCE_OVERVIEW
      );
    }
  };

  const navigateToApiTryout = (event: React.MouseEvent) => {
    event.stopPropagation();
    trackTryOutClickFromAPICard(apiId);
    history.push(
      PATH_SEPARATOR +
        organizationHandle +
        RESOURCE_API +
        PATH_SEPARATOR +
        apiId +
        RESOURCE_API_TRYOUT
    );
  };

  const navigateToApiDocs = (event: React.MouseEvent) => {
    event.stopPropagation();
    trackDocumentationClickFromAPICard(apiId);
    history.push(
      PATH_SEPARATOR +
        organizationHandle +
        RESOURCE_API +
        PATH_SEPARATOR +
        apiId +
        RESOURCE_API_DOCUMENTS
    );
  };

  const ApiCardContent = (): JSX.Element | null => {
    return apiSummary ? (
      <CardContent
        className={clsx(classes.cardContent, 'api-card-content')}>
        <Typography>{truncatedApiSummary}</Typography>
      </CardContent>
    ) : null;
  };

  const ApiCardAction = (): JSX.Element => {
    return (
      <CardActions classes={{
        root: clsx(classes.cardActionRoot, { [classes.cardActionRootPrototype]: lifeCycleStatus === 'PROTOTYPED' })
      }}>
        {apiTitle ? (
          <Box>
            {isApimTokenObtained && (
            <Button
              onClick={navigateToApiTryout}
              className={classes.tryOutButton}
              variant='outlined'
            >
              <FormattedMessage
                id='api.apiList.apiCard.buttons.apiTryout'
                defaultMessage='Try Out'
              />
            </Button>
            )}
            <Button onClick={navigateToApiDocs} className={classes.docsButton}>
              <FormattedMessage
                id='api.apiList.apiCard.buttons.apiDocumentation'
                defaultMessage='Documentation'
              />
            </Button>
          </Box>
        ) : (
          <Skeleton
            animation='wave'
            height={40}
            width='100%'
            className={classes.cardActionSkeleton}
          />
        )}
      </CardActions>
    );
  };

  const ApiCardHeader = (): JSX.Element => {
    return (
      <CardHeader 
        className={clsx(classes.cardHeader, { [classes.cardHeaderPrototype]: lifeCycleStatus === 'PROTOTYPED' })}
        classes={{
          title: classes.cardHeaderTitle,
          subheader: classes.cardheaderSubTitle,
        }} 
        avatar={
          apiImageUrl ? (
            <Avatar
              aria-label='api-icon-image'
              src={apiImageUrl}
              alt={apiTitle}
              className={classes.apiIconImage}
            />
          ) : (
            <Skeleton
              animation='wave'
              variant='circle'
              width={53}
              height={53}
            />
          )
        }
        title={
          apiTitle ? (
            <Box display='flex' alignItems='center'>
              <Box display='flex' flexDirection='column'>
                <Box>
                  <Grid container>
                    <Grid item>
                      <Typography className={classes.apiType}>
                        {apiType === API_TYPE_WS ? (
                            <FormattedMessage
                              id='components.api-list.ApiCard.apitype.ws'
                              defaultMessage='WebSocket'
                            />
                          ) : apiType === API_TYPE_GRAPHQL ? (
                            <FormattedMessage
                              id='components.api-list.ApiCard.apitype.graphQL'
                              defaultMessage='GraphQL'
                            />
                          ) : (
                            <FormattedMessage
                              id='components.api-list.ApiCard.apitype.rest'
                              defaultMessage='REST'
                            />
                          )}
                      </Typography>
                    </Grid>
                    <Grid item className={classes.lsStatusWrapper}>
                      <div>
                        {lifeCycleStatus === 'PROTOTYPED' && (
                          <span className={classes.lifecycleStatus}>
                            <FormattedMessage
                              id='api.apiList.apiCard.labels.lifecycleStatus.preRelease'
                              defaultMessage='Pre-Release'
                            />
                          </span>
                        )}
                      </div>
                    </Grid>
                  </Grid>
                </Box>
                <Tooltip title={apiTitle} placement='top' arrow={true}>
                  <Typography className={classes.apiTitle}>{apiTitle}</Typography>
                </Tooltip>
              </Box>
              {isInternalAPI && 
              <Box pl={1}>
                <Tooltip
                  title={intl.formatMessage({
                    id: 'api.apiList.apiCard.internal.api.tooltip.text',
                    defaultMessage: 'This is an internal API',
                  })}
                  placement='bottom'
                  interactive={true}
                  data-testid='internal-api-tooltip'
                >
                  <img 
                    src="/themes/default/images/internal.svg" 
                    alt="interal-api-icon"
                  />
                </Tooltip>
              </Box>}
            </Box>
          ) : (
            <Skeleton
              animation='wave'
              height={10}
              width='80%'
              className={classes.cardHeaderTitleSkeleton}
            />
          )
        }>
      </CardHeader>
    );
  };

  const VersionInfo = () => {
    return (
      apiTitle ? (
            <div className={clsx(classes.versionInfo, 'versionInfo')}>
              <Grid container className={classes.versionWrapper} spacing={2}>
                <Grid item>
                  <Typography>
                    <FormattedMessage
                      id='api.apiList.apiCard.labels.apiVersion'
                      defaultMessage='Version : {apiVersion}'
                      values={{
                        apiVersion: Settings.intelligentRoutingFeatureEnabled && apiVersion.startsWith("v")
                        ? apiVersion.split(".")[0]
                        : apiVersion
                      }}
                    />
                  </Typography>
                </Grid>
                <Grid item>|</Grid>
                <Grid item>
                  <Rating
                    name='size-small'
                    value={apiRating}
                    size='small'
                    readOnly
                    className={classes.rating}
                  />
                </Grid>
              </Grid>
              
            </div>
          ) : (
            <Skeleton animation='wave' height={10} width='40%' />
          )
    )
  }

  return (
    <div>
      <div className={classes.cardDiv}>
        <Card
          className={apiTitle ? classes.card : classes.noHoverCard}
          onClick={navigateToApiOverview}
          data-testid={testId}
        >
          <ApiCardHeader />
          <Box className={classes.cardContentWrapper}>
            <ApiCardContent />
            <VersionInfo/>
          </Box>
          <ApiCardAction />
        </Card>
      </div>
    </div>
  );
}

export default React.memo(ApiCard);
